import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  CalendarToday,
  AttachMoney,
  Edit,
  CheckCircle,
  Schedule,
  Block,
  InfoOutlined
} from '@mui/icons-material';
import dataInventoryTimelineService, {
  DataInventoryTimeline,
  TimelinePhase
} from '../../services/dataInventoryTimelineService';
import { uswdsColorScheme } from '../../styles/colorSchemes';

const DataInventoryTimelineComponent: React.FC = () => {
  const [timeline, setTimeline] = useState<DataInventoryTimeline | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [newStartDate, setNewStartDate] = useState<string>('');
  const [editPhaseDialogOpen, setEditPhaseDialogOpen] = useState<boolean>(false);
  const [selectedPhase, setSelectedPhase] = useState<TimelinePhase | null>(null);
  const [editedPhase, setEditedPhase] = useState<Partial<TimelinePhase>>({});
  const [activeTab, setActiveTab] = useState<number>(0);
  
  // Activity edit state
  const [editActivityDialogOpen, setEditActivityDialogOpen] = useState<boolean>(false);
  const [selectedActivity, setSelectedActivity] = useState<any>(null);
  const [editedActivity, setEditedActivity] = useState<any>({});
  
  // Deliverable edit state
  const [editDeliverableDialogOpen, setEditDeliverableDialogOpen] = useState<boolean>(false);
  const [selectedDeliverable, setSelectedDeliverable] = useState<any>(null);
  const [editedDeliverable, setEditedDeliverable] = useState<any>({});
  
  // Milestone edit state
  const [editMilestoneDialogOpen, setEditMilestoneDialogOpen] = useState<boolean>(false);
  const [selectedMilestone, setSelectedMilestone] = useState<any>(null);
  const [editedMilestone, setEditedMilestone] = useState<any>({});

  useEffect(() => {
    loadTimeline();
  }, []);

  const loadTimeline = async () => {
    try {
      setLoading(true);
      // Add cache-busting timestamp
      const data = await dataInventoryTimelineService.getTimeline();
      console.log('Timeline loaded:', data);
      setTimeline(data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading timeline:', err);
      setError(err.message || 'Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStartDate = () => {
    if (timeline) {
      const date = new Date(timeline.projectStartDate);
      setNewStartDate(date.toISOString().split('T')[0]);
      setEditDialogOpen(true);
    }
  };

  const handleSaveStartDate = async () => {
    if (!timeline || !newStartDate) return;

    try {
      setLoading(true);
      await dataInventoryTimelineService.updateStartDate(
        timeline._id,
        new Date(newStartDate),
        'User'
      );
      await loadTimeline();
      setEditDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update start date');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPhase = (phase: TimelinePhase) => {
    setSelectedPhase(phase);
    setEditedPhase({
      phaseName: phase.phaseName,
      description: phase.description,
      budget: phase.budget
    });
    setEditPhaseDialogOpen(true);
  };

  const handleSavePhase = async () => {
    if (!timeline || !selectedPhase || !selectedPhase._id) return;

    try {
      setLoading(true);
      setError(null);
      
      console.log('Saving phase:', {
        timelineId: timeline._id,
        phaseId: selectedPhase._id,
        updates: editedPhase
      });
      
      const result = await dataInventoryTimelineService.updatePhase(
        timeline._id,
        selectedPhase._id,
        editedPhase
      );
      
      console.log('Phase updated successfully:', result);
      
      // Force reload from server
      await loadTimeline();
      
      setEditPhaseDialogOpen(false);
      setSelectedPhase(null);
      setEditedPhase({});
    } catch (err: any) {
      console.error('Error updating phase:', err);
      setError(err.message || 'Failed to update phase');
      setLoading(false);
      // Don't close dialog on error
      return;
    } finally {
      setLoading(false);
    }
  };

  const handleEditActivity = (activity: any, phaseId: string) => {
    setSelectedActivity({ ...activity, phaseId });
    setEditedActivity({
      name: activity.name,
      description: activity.description,
      owner: activity.owner,
      status: activity.status
    });
    setEditActivityDialogOpen(true);
  };

  const handleSaveActivity = async () => {
    if (!timeline || !selectedActivity || !selectedActivity._id) return;

    try {
      setLoading(true);
      setError(null);
      
      await dataInventoryTimelineService.updateActivity(
        timeline._id,
        selectedActivity.phaseId,
        selectedActivity._id,
        editedActivity
      );
      
      await loadTimeline();
      setEditActivityDialogOpen(false);
      setSelectedActivity(null);
      setEditedActivity({});
    } catch (err: any) {
      console.error('Error updating activity:', err);
      setError(err.message || 'Failed to update activity');
      setLoading(false);
      return;
    } finally {
      setLoading(false);
    }
  };

  const handleEditDeliverable = (deliverable: any, phaseId: string) => {
    setSelectedDeliverable({ ...deliverable, phaseId });
    setEditedDeliverable({
      name: deliverable.name,
      description: deliverable.description,
      dueDate: new Date(deliverable.dueDate).toISOString().split('T')[0],
      status: deliverable.status
    });
    setEditDeliverableDialogOpen(true);
  };

  const handleSaveDeliverable = async () => {
    if (!timeline || !selectedDeliverable || !selectedDeliverable._id) return;

    try {
      setLoading(true);
      setError(null);
      
      await dataInventoryTimelineService.updateDeliverable(
        timeline._id,
        selectedDeliverable.phaseId,
        selectedDeliverable._id,
        {
          ...editedDeliverable,
          dueDate: new Date(editedDeliverable.dueDate)
        }
      );
      
      await loadTimeline();
      setEditDeliverableDialogOpen(false);
      setSelectedDeliverable(null);
      setEditedDeliverable({});
    } catch (err: any) {
      console.error('Error updating deliverable:', err);
      setError(err.message || 'Failed to update deliverable');
      setLoading(false);
      return;
    } finally {
      setLoading(false);
    }
  };

  const handleEditMilestone = (milestone: any, phaseId: string) => {
    setSelectedMilestone({ ...milestone, phaseId });
    setEditedMilestone({
      name: milestone.name,
      date: new Date(milestone.date).toISOString().split('T')[0],
      achieved: milestone.achieved
    });
    setEditMilestoneDialogOpen(true);
  };

  const handleSaveMilestone = async () => {
    if (!timeline || !selectedMilestone || !selectedMilestone._id) return;

    try {
      setLoading(true);
      setError(null);
      
      await dataInventoryTimelineService.updateMilestone(
        timeline._id,
        selectedMilestone.phaseId,
        selectedMilestone._id,
        {
          ...editedMilestone,
          date: new Date(editedMilestone.date)
        }
      );
      
      await loadTimeline();
      setEditMilestoneDialogOpen(false);
      setSelectedMilestone(null);
      setEditedMilestone({});
    } catch (err: any) {
      console.error('Error updating milestone:', err);
      setError(err.message || 'Failed to update milestone');
      setLoading(false);
      return;
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'Completed':
        return 'success'; // USWDS Green
      case 'In Progress':
        return 'primary'; // USWDS Blue
      case 'Blocked':
      case 'Delayed':
        return 'error'; // USWDS Red
      case 'Not Started':
        return 'default'; // USWDS Gray
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle />;
      case 'In Progress':
        return <Schedule />;
      case 'Blocked':
      case 'Delayed':
        return <Block />;
      default:
        return <Schedule />;
    }
  };

  if (loading && !timeline) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !timeline) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!timeline) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">No timeline data available</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          background: uswdsColorScheme.gradients.header,
          color: uswdsColorScheme.text.inverse,
          p: 4,
          borderRadius: 2,
          mb: 4
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ color: uswdsColorScheme.text.inverse }}>
          📋 {timeline.projectName}
        </Typography>
        <Typography variant="subtitle1" sx={{ color: uswdsColorScheme.text.inverse, opacity: 0.95 }}>
          Interactive Project Timeline | Click to Edit
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Project Info Card */}
      <Card
        sx={{
          mb: 4,
          background: uswdsColorScheme.gradients.card,
          borderLeft: `6px solid ${uswdsColorScheme.primary}`,
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': {
            transform: 'translateX(5px)',
            boxShadow: 4
          }
        }}
        onClick={handleEditStartDate}
      >
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ color: uswdsColorScheme.primary }}>
              🎯 Project Overview
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={(e) => {
                e.stopPropagation();
                handleEditStartDate();
              }}
            >
              Edit Start Date
            </Button>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box sx={{ bgcolor: uswdsColorScheme.surface, p: 2, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: uswdsColorScheme.text.secondary }}>
                  Start Date
                </Typography>
                <Typography variant="h6" sx={{ color: uswdsColorScheme.primary }}>
                  {formatDate(timeline.projectStartDate)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Box sx={{ bgcolor: uswdsColorScheme.surface, p: 2, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: uswdsColorScheme.text.secondary }}>
                  End Date
                </Typography>
                <Typography variant="h6" sx={{ color: uswdsColorScheme.primary }}>
                  {formatDate(timeline.projectEndDate)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Box sx={{ bgcolor: uswdsColorScheme.surface, p: 2, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: uswdsColorScheme.text.secondary }}>
                  Duration
                </Typography>
                <Typography variant="h6" sx={{ color: uswdsColorScheme.primary }}>
                  {timeline.totalDuration} weeks
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Box sx={{ bgcolor: uswdsColorScheme.surface, p: 2, borderRadius: 1 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="caption" sx={{ color: uswdsColorScheme.text.secondary }}>
                    Total Budget
                  </Typography>
                  <Tooltip 
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>Budget Breakdown:</Typography>
                        <Typography variant="body2">Phase 1: {formatCurrency(110000)} (27%)</Typography>
                        <Typography variant="body2">Phase 2: {formatCurrency(80000)} (20%)</Typography>
                        <Typography variant="body2">Phase 3: {formatCurrency(150000)} (37%)</Typography>
                        <Typography variant="body2">Phase 4: {formatCurrency(70000)} (17%)</Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                          Total: {formatCurrency(timeline.totalBudget)}
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                          Includes personnel (82%), tools (12%), analysis (2%), overhead (4%)
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <IconButton size="small" sx={{ p: 0, color: uswdsColorScheme.info }}>
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="h6" sx={{ color: uswdsColorScheme.primary }}>
                  {formatCurrency(timeline.totalBudget)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Box sx={{ bgcolor: uswdsColorScheme.surface, p: 2, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: uswdsColorScheme.text.secondary }}>
                  Status
                </Typography>
                <Typography variant="h6" sx={{ color: uswdsColorScheme.primary }}>
                  {timeline.status}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography variant="caption" sx={{ mt: 2, display: 'block', color: uswdsColorScheme.info }}>
            💡 Click to edit start date (all dates will recalculate)
          </Typography>
        </CardContent>
      </Card>

      {/* Phases - Tabbed Interface */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              color: uswdsColorScheme.text.secondary,
              fontWeight: 600,
              fontSize: '1rem'
            },
            '& .Mui-selected': {
              color: uswdsColorScheme.primary
            }
          }}
        >
          {timeline.phases.map((phase, index) => (
            <Tab 
              key={phase._id} 
              label={`Phase ${phase.phaseNumber}: ${phase.phaseName.split('(')[0].trim()}`}
              icon={
                <Box
                  sx={{
                    background: activeTab === index ? uswdsColorScheme.gradients.header : uswdsColorScheme.background,
                    color: activeTab === index ? uswdsColorScheme.text.inverse : uswdsColorScheme.text.primary,
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    mb: 0.5
                  }}
                >
                  {phase.phaseNumber}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Active Phase Content */}
      {timeline.phases[activeTab] && (
        <Card
          sx={{
            mb: 3,
            border: '2px solid #e0e0e0',
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: 4,
              borderColor: uswdsColorScheme.primary
            }
          }}
        >
          <CardContent>
            {/* Phase Header */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
              <Box display="flex" gap={2} alignItems="flex-start">
                <Box
                  sx={{
                    background: uswdsColorScheme.gradients.header,
                    color: uswdsColorScheme.text.inverse,
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '1.5rem'
                  }}
                >
                  {timeline.phases[activeTab].phaseNumber}
                </Box>
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h5" sx={{ color: uswdsColorScheme.primary }}>
                      {timeline.phases[activeTab].phaseName}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => handleEditPhase(timeline.phases[activeTab])}
                      sx={{ color: uswdsColorScheme.primary }}
                      aria-label="Edit phase"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ color: uswdsColorScheme.text.secondary }}>
                    {timeline.phases[activeTab].weekRange}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" gap={2} flexWrap="wrap">
                <Box sx={{ bgcolor: uswdsColorScheme.background, p: 1.5, borderRadius: 1, borderLeft: `4px solid ${uswdsColorScheme.primary}` }}>
                  <Typography variant="caption" sx={{ color: uswdsColorScheme.text.secondary }}>
                    Start Date
                  </Typography>
                  <Typography variant="body2" fontWeight="600" sx={{ color: uswdsColorScheme.primary }}>
                    {formatDate(timeline.phases[activeTab].startDate)}
                  </Typography>
                </Box>

                <Box sx={{ bgcolor: uswdsColorScheme.background, p: 1.5, borderRadius: 1, borderLeft: `4px solid ${uswdsColorScheme.primary}` }}>
                  <Typography variant="caption" sx={{ color: uswdsColorScheme.text.secondary }}>
                    End Date
                  </Typography>
                  <Typography variant="body2" fontWeight="600" sx={{ color: uswdsColorScheme.primary }}>
                    {formatDate(timeline.phases[activeTab].endDate)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    background: uswdsColorScheme.gradients.accent,
                    p: 1.5,
                    borderRadius: 1,
                    borderLeft: `4px solid ${uswdsColorScheme.warning}`
                  }}
                >
                  <Typography variant="caption" sx={{ color: uswdsColorScheme.text.primary }}>
                    Budget
                  </Typography>
                  <Typography variant="body2" fontWeight="700" sx={{ color: uswdsColorScheme.text.primary }}>
                    {formatCurrency(timeline.phases[activeTab].budget)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Phase Description */}
            <Box sx={{ bgcolor: uswdsColorScheme.background, p: 2, borderRadius: 1, mb: 3 }}>
              <Typography variant="body2" sx={{ color: uswdsColorScheme.text.secondary }}>
                {timeline.phases[activeTab].description}
              </Typography>
            </Box>

            {/* Click to Edit Indicator */}
            <Typography variant="caption" sx={{ color: uswdsColorScheme.info, fontStyle: 'italic', display: 'block', mb: 2 }}>
              💡 Click any card below to edit activities, deliverables, or milestones. Click the edit icon (✏️) to edit phase details.
            </Typography>

            {/* Activities */}
            {timeline.phases[activeTab].activities && timeline.phases[activeTab].activities.length > 0 && (
              <Box mb={3}>
                <Typography variant="h6" sx={{ color: uswdsColorScheme.primary }} gutterBottom>
                  📋 Activities ({timeline.phases[activeTab].activities.length})
                </Typography>
                <Grid container spacing={2}>
                  {timeline.phases[activeTab].activities.map((activity, idx) => (
                    <Grid item xs={12} md={6} lg={4} key={idx}>
                      <Card
                        onClick={() => handleEditActivity(activity, timeline.phases[activeTab]._id!)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleEditActivity(activity, timeline.phases[activeTab]._id!);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`Edit activity: ${activity.name}`}
                        sx={{
                          bgcolor: uswdsColorScheme.surface,
                          borderLeft: `4px solid ${uswdsColorScheme.success}`,
                          transition: 'all 0.3s',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: uswdsColorScheme.background,
                            transform: 'translateX(5px)',
                            boxShadow: 3
                          },
                          '&:focus': {
                            outline: `3px solid ${uswdsColorScheme.primary}`,
                            outlineOffset: '2px'
                          }
                        }}
                      >
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Typography variant="subtitle2" sx={{ color: uswdsColorScheme.success, flex: 1 }} gutterBottom>
                              {activity.name}
                            </Typography>
                            <Edit fontSize="small" sx={{ color: uswdsColorScheme.success, ml: 1 }} />
                          </Box>
                          <Typography variant="body2" sx={{ color: uswdsColorScheme.text.secondary }} gutterBottom>
                            {activity.description}
                          </Typography>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                            <Typography variant="caption" sx={{ color: uswdsColorScheme.text.secondary }}>
                              👤 {activity.owner}
                            </Typography>
                            <Chip
                              label={activity.status}
                              size="small"
                              color={getStatusColor(activity.status)}
                              icon={getStatusIcon(activity.status)}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Deliverables */}
            {timeline.phases[activeTab].deliverables && timeline.phases[activeTab].deliverables.length > 0 && (
              <Box mb={3}>
                <Typography variant="h6" color="primary" gutterBottom>
                  📦 Deliverables ({timeline.phases[activeTab].deliverables.length})
                </Typography>
                <Grid container spacing={2}>
                  {timeline.phases[activeTab].deliverables.map((deliverable, idx) => (
                    <Grid item xs={12} md={6} lg={4} key={idx}>
                      <Card
                        onClick={() => handleEditDeliverable(deliverable, timeline.phases[activeTab]._id!)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleEditDeliverable(deliverable, timeline.phases[activeTab]._id!);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`Edit deliverable: ${deliverable.name}`}
                        sx={{
                          bgcolor: '#f8f9fa',
                          borderLeft: '4px solid #2196f3',
                          transition: 'all 0.3s',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: '#e3f2fd',
                            transform: 'translateX(5px)',
                            boxShadow: 3
                          },
                          '&:focus': {
                            outline: `3px solid ${uswdsColorScheme.primary}`,
                            outlineOffset: '2px'
                          }
                        }}
                      >
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Typography variant="subtitle2" color="primary" sx={{ flex: 1 }} gutterBottom>
                              {deliverable.name}
                            </Typography>
                            <Edit fontSize="small" color="primary" sx={{ ml: 1 }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {deliverable.description}
                          </Typography>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                            <Typography variant="caption" color="text.secondary">
                              📅 Due: {formatDate(deliverable.dueDate)}
                            </Typography>
                            <Chip
                              label={deliverable.status}
                              size="small"
                              color={getStatusColor(deliverable.status)}
                              icon={getStatusIcon(deliverable.status)}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Milestones */}
            {timeline.phases[activeTab].milestones && timeline.phases[activeTab].milestones.length > 0 && (
              <Box>
                <Typography variant="h6" color="primary" gutterBottom>
                  🎯 Milestones ({timeline.phases[activeTab].milestones.length})
                </Typography>
                <Grid container spacing={2}>
                  {timeline.phases[activeTab].milestones.map((milestone, idx) => (
                    <Grid item xs={12} md={6} lg={4} key={idx}>
                      <Card
                        onClick={() => handleEditMilestone(milestone, timeline.phases[activeTab]._id!)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleEditMilestone(milestone, timeline.phases[activeTab]._id!);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`Edit milestone: ${milestone.name}`}
                        sx={{
                          bgcolor: '#f8f9fa',
                          borderLeft: `4px solid ${milestone.achieved ? '#4caf50' : '#9e9e9e'}`,
                          transition: 'all 0.3s',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: milestone.achieved ? '#e8f5e9' : '#f5f5f5',
                            transform: 'translateX(5px)',
                            boxShadow: 3
                          },
                          '&:focus': {
                            outline: `3px solid ${uswdsColorScheme.primary}`,
                            outlineOffset: '2px'
                          }
                        }}
                      >
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Typography variant="subtitle2" color={milestone.achieved ? 'success.main' : 'text.secondary'} sx={{ flex: 1 }} gutterBottom>
                              {milestone.name}
                            </Typography>
                            <Edit fontSize="small" sx={{ color: milestone.achieved ? '#4caf50' : '#9e9e9e', ml: 1 }} />
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                            <Typography variant="caption" color="text.secondary">
                              📅 {formatDate(milestone.date)}
                            </Typography>
                            <Chip
                              label={milestone.achieved ? '✅ Achieved' : '⏳ Pending'}
                              size="small"
                              color={milestone.achieved ? 'success' : 'default'}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Start Date Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>📅 Edit Project Start Date</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="date"
            label="New Start Date"
            value={newStartDate}
            onChange={(e) => setNewStartDate(e.target.value)}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Changing the start date will automatically recalculate all phase dates.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveStartDate} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : '💾 Save & Recalculate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Phase Dialog */}
      <Dialog open={editPhaseDialogOpen} onClose={() => setEditPhaseDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>✏️ Edit Phase: {selectedPhase?.phaseName}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Phase Name"
              value={editedPhase.phaseName || ''}
              onChange={(e) => setEditedPhase({ ...editedPhase, phaseName: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={editedPhase.description || ''}
              onChange={(e) => setEditedPhase({ ...editedPhase, description: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Budget"
              type="number"
              value={editedPhase.budget || ''}
              onChange={(e) => setEditedPhase({ ...editedPhase, budget: Number(e.target.value) })}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              }}
            />
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Changes will be saved to the database immediately.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPhaseDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePhase} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : '💾 Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Activity Dialog */}
      <Dialog open={editActivityDialogOpen} onClose={() => setEditActivityDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>✏️ Edit Activity: {selectedActivity?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Activity Name"
              value={editedActivity.name || ''}
              onChange={(e) => setEditedActivity({ ...editedActivity, name: e.target.value })}
              sx={{ mb: 2 }}
              autoFocus
            />
            <TextField
              fullWidth
              label="Description"
              value={editedActivity.description || ''}
              onChange={(e) => setEditedActivity({ ...editedActivity, description: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Owner"
              value={editedActivity.owner || ''}
              onChange={(e) => setEditedActivity({ ...editedActivity, owner: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editedActivity.status || 'Not Started'}
                onChange={(e) => setEditedActivity({ ...editedActivity, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="Not Started">Not Started</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Blocked">Blocked</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Changes will be saved to the database immediately.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditActivityDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveActivity} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : '💾 Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Deliverable Dialog */}
      <Dialog open={editDeliverableDialogOpen} onClose={() => setEditDeliverableDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>✏️ Edit Deliverable: {selectedDeliverable?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Deliverable Name"
              value={editedDeliverable.name || ''}
              onChange={(e) => setEditedDeliverable({ ...editedDeliverable, name: e.target.value })}
              sx={{ mb: 2 }}
              autoFocus
            />
            <TextField
              fullWidth
              label="Description"
              value={editedDeliverable.description || ''}
              onChange={(e) => setEditedDeliverable({ ...editedDeliverable, description: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="date"
              label="Due Date"
              value={editedDeliverable.dueDate || ''}
              onChange={(e) => setEditedDeliverable({ ...editedDeliverable, dueDate: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editedDeliverable.status || 'Not Started'}
                onChange={(e) => setEditedDeliverable({ ...editedDeliverable, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="Not Started">Not Started</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Delayed">Delayed</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Changes will be saved to the database immediately.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDeliverableDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveDeliverable} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : '💾 Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Milestone Dialog */}
      <Dialog open={editMilestoneDialogOpen} onClose={() => setEditMilestoneDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>✏️ Edit Milestone: {selectedMilestone?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Milestone Name"
              value={editedMilestone.name || ''}
              onChange={(e) => setEditedMilestone({ ...editedMilestone, name: e.target.value })}
              sx={{ mb: 2 }}
              autoFocus
            />
            <TextField
              fullWidth
              type="date"
              label="Date"
              value={editedMilestone.date || ''}
              onChange={(e) => setEditedMilestone({ ...editedMilestone, date: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editedMilestone.achieved || false}
                  onChange={(e) => setEditedMilestone({ ...editedMilestone, achieved: e.target.checked })}
                />
              }
              label="Milestone Achieved"
            />
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Changes will be saved to the database immediately.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMilestoneDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveMilestone} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : '💾 Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DataInventoryTimelineComponent;
